import { useMemo, useState } from 'react';
import { StyledProductCard } from './style';
import { ProductData } from 'types/ProductData';
import { useIntl } from 'react-intl';
import AmountSelector from '../CartAmountSelector/CartAmountSelector';
import { ProductPackageUnit } from '../../utils/productCardHelpers';
import { ProductCardPrice } from './ProductCardPrice';
import { Icon } from '../Icon';
import { Tooltip } from '../Tooltip';
import { SubtleTooltip } from '../Tooltip/SubtleTooltip';
import { WholesaleGateModal } from '../Modal/WholesaleGateModal/WholesaleGateModal';
import {
  CartActionType,
  CartProduct,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import { FallbackImage } from '../UploadImage/fallBack/FallBackImage';
import useMarketType from '../../utils/useMarketType';
import { publicPath } from 'enums/publicRoutes';
import { useAuth } from '../../hooks/useAuth';
import { useBreakpoint } from 'utils/useBreakpoint';
import { useNavigate } from 'react-router-dom';
import { qualityMarkLogoClassName } from 'utils/qualityMarkHelpers';

export interface ProductCardProps {
  product?: ProductData;
  imageUrl?: string;
  plainImageUrl?: string;
  className?: string;
  hideCart?: boolean;
  hoverable?: boolean;
  farmView?: boolean;
}

export const Card = ({
  product,
  imageUrl,
  plainImageUrl,
  className,
  hideCart,
  hoverable,
  farmView,
}: ProductCardProps) => {
  const intl = useIntl();
  const cartDispatch = useCartDispatch();
  const { farmProducts } = useCartState();
  const { user } = useAuth();
  const { isEmarket } = useMarketType();
  const { isXsUp } = useBreakpoint();

  const productAmount = product?.product_amounts?.find((amount) =>
    isEmarket ? amount.type === 'EMARKET' : amount.type !== 'EMARKET'
  );
  const reservation = productAmount?.wholesale_reserve;

  const matchedCartProduct: CartProduct | undefined = farmProducts
    .flatMap((farm) => farm.products)
    .find((cartProduct) => cartProduct?.cartProduct?.id === product?.id);

  const amountCart = matchedCartProduct ? matchedCartProduct.amount : 0;

  const isAddToCartAllowed = useMemo(() => {
    if (!product) return false;

    const stockCount = productAmount?.storage_stock_count
      ? productAmount?.storage_stock_count
      : (productAmount?.wholesale_reserve?.amount_to ?? 0);
    const limitMin = productAmount?.storage_limit_min ?? 0;
    const step = productAmount?.step ?? 0;

    return (
      !product.hidden &&
      !!productAmount?.storage_unit &&
      stockCount > 0 &&
      stockCount >= limitMin &&
      stockCount >= step &&
      (!!Number(productAmount?.wholesale_reserve?.amount_to) ||
        stockCount > 0) &&
      (isEmarket ? product.emarket : product?.wholesale)
    );
  }, [product, productAmount, isEmarket]);

  const productHasDiscount = !!(
    productAmount?.storage_stock_discount_price &&
    productAmount?.storage_stock_price &&
    productAmount.storage_stock_discount_price <
      productAmount.storage_stock_price
  );

  const discountPercent = productHasDiscount
    ? Math.round(
        ((productAmount!.storage_stock_price! -
          productAmount!.storage_stock_discount_price!) /
          productAmount!.storage_stock_price!) *
          100
      )
    : 0;

  const uniqueMarks = product?.qualitymarks?.filter(
    (q, i, arr) =>
      arr.findIndex((x) => x.quality_mark_id === q.quality_mark_id) === i
  );

  const navigate = useNavigate();
  const farmName =
    product?.farm?.brand ?? product?.farm_name ?? product?.farm?.name;

  const handleOnClick = () => {
    if (product?.id) {
      navigate(
        isEmarket
          ? publicPath.product(product.slug, product.id)
          : publicPath.wholesaleProduct(product.slug, product.id)
      );
    }
  };

  const handleFarmClick = () => {
    navigate(
      isEmarket
        ? publicPath.farm(product?.farm?.slug, product?.farm_id)
        : publicPath.wholesaleFarm(product?.farm?.slug, product?.farm_id)
    );
  };

  const [isGateModalOpen, setIsGateModalOpen] = useState(false);
  const hasWholesaleAccess =
    !!user?.authenticated && !!user?.farm?.has_wholesale_contract;

  const addToCart = () => {
    if (!isEmarket && !hasWholesaleAccess) {
      setIsGateModalOpen(true);
      return;
    }
    if (!isAddToCartAllowed) return;
    cartDispatch({
      type: CartActionType.ADD_ITEM,
      product: product,
      amount: productAmount?.step ?? 1,
    });
  };

  const showCartControls = !hideCart && isAddToCartAllowed;
  const showUnavailable =
    !hideCart && !showCartControls && isEmarket && !!product?.emarket;

  return (
    <StyledProductCard
      className={`${className ?? ''}${!isEmarket ? ' wholesale' : ''}${
        hoverable ? ' hoverable' : ''
      }${farmView ? ' farm-view' : ''}`}
    >
      <div className={'product-card-cover'} onClick={handleOnClick}>
        <FallbackImage
          src={plainImageUrl ? plainImageUrl : imageUrl ? imageUrl : ''}
          preview={false}
          type={'ProductCard'}
          className={'product-card-image'}
          alt={product?.name}
        />
        <div className={'product-card-tags'}>
          {product?.is_top_bought_product && (
            <div className={'product-card-tag top'}>
              {intl.formatMessage({ id: 'product.top_price' })}
            </div>
          )}
          {discountPercent > 0 && (
            <div className={'product-card-tag discount'}>
              -{discountPercent}%
            </div>
          )}
          {!!uniqueMarks?.length && (
            <div className={'product-card-qmarks'}>
              {uniqueMarks.map((qmark, index) => (
                <Tooltip key={index} title={qmark.quality_mark?.name}>
                  <span>
                    <FallbackImage
                      className={`product-card-qmark ${qualityMarkLogoClassName(
                        qmark.quality_mark?.slug
                      )}`}
                      src={
                        qmark.quality_mark?.logo ? qmark.quality_mark.logo : ''
                      }
                      type={'notFound'}
                      preview={false}
                      alt={qmark.quality_mark?.name}
                    />
                  </span>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
        <SubtleTooltip className={'product-card-name'} title={product?.name}>
          {product?.name} <ProductPackageUnit productAmount={productAmount} />
        </SubtleTooltip>
      </div>
      <div className={'product-card-body'}>
        {!farmView && !!farmName && (
          <SubtleTooltip
            className={'product-card-farm'}
            title={farmName}
            onClick={handleFarmClick}
          >
            <Icon
              name={'location-pin-icon'}
              className={'product-card-farm-pin'}
            />
            <span className={'product-card-farm-name'}>{farmName}</span>
          </SubtleTooltip>
        )}
        {!isEmarket && (
          <div className={'product-card-availability'}>
            {!!productAmount?.storage_stock_count && (
              <div className={'product-card-availability-row'}>
                <span>
                  {intl.formatMessage({ id: 'general.availability_amount' })}
                </span>
                <span>
                  {productAmount?.storage_stock_count}{' '}
                  {productAmount?.storage_unit}
                </span>
              </div>
            )}
            {!!reservation?.amount_to && (
              <div className={'product-card-availability-reserve'}>
                {intl.formatMessage({ id: 'product.possibility_to_reserve' })} (
                {reservation.amount_from ? `${reservation.amount_from}-` : ''}
                {reservation.amount_to} {productAmount?.storage_unit})
              </div>
            )}
          </div>
        )}
        <ProductCardPrice productAmount={productAmount} amount={amountCart} />
        {showUnavailable && (
          <div className={'product-card-cart'}>
            <button
              className={'product-card-add-btn unavailable'}
              disabled
              aria-disabled
            >
              <Icon name={'bag-tick-white'} />
              {intl.formatMessage({ id: 'button.notAvailable' })}
            </button>
          </div>
        )}
        {showCartControls && (
          <div className={'product-card-cart'}>
            {amountCart === 0 ? (
              <button
                className={'product-card-add-btn'}
                onClick={() => addToCart()}
              >
                <Icon name={'bag-tick-white'} />
                {intl.formatMessage({
                  id: isXsUp
                    ? 'button.addToCartDesktop'
                    : 'button.addToCartMobile',
                })}
              </button>
            ) : (
              <AmountSelector
                product={product}
                amountCart={amountCart}
                unitType={productAmount?.storage_unit}
              />
            )}
          </div>
        )}
      </div>
      {!isEmarket && (
        <WholesaleGateModal
          open={isGateModalOpen}
          onClose={() => setIsGateModalOpen(false)}
        />
      )}
    </StyledProductCard>
  );
};
